import React, { useState } from 'react';
import { 
  Home, 
  Users, 
  BarChart3, 
  Settings, 
  LogOut,
  X,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  Building2,
  Package,
  Percent,
  UsersRound,
  Receipt,
  Wallet,
  FileText,
  Target,
  ClipboardList,
  UserPlus,
  BookOpen,
  Banknote
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const Sidebar = ({ isOpen, onClose, onNavigate, currentView, user, onLogout }) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { isDarkMode } = useTheme();

  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // Check if user has admin privileges
  const isAdmin = user?.role === 'Superadmin' || user?.role === 'Admin';
  
  // Check if user belongs to a group (GL, TL, Agent)
  const hasGroup = user?.role === 'Group Leader' || user?.role === 'Team Leader' || user?.role === 'Agent';

  const menuItems = [
    { icon: Home, label: 'Dashboard', id: 'dashboard' },
    { icon: Wallet, label: isAdmin ? 'Commission Management' : 'My Commissions', id: 'my-commissions' },
    { icon: TrendingUp, label: 'Analytics', id: 'analytics' },
    { icon: FileText, label: 'Reports', id: 'reports' },
  ];

  // Add My Group menu for GL, TL, Agent
  if (hasGroup) {
    menuItems.push({ icon: UsersRound, label: 'My Group', id: 'my-group' });
  }

  // Add Sales Management and Dividend Payment for Admin
  if (isAdmin) {
    menuItems.splice(2, 0,
      { icon: Receipt, label: 'Sales Management', id: 'sales-management' },
      { icon: Banknote, label: 'Dividend Payment', id: 'dividend-payment' }
    );
  }

  const adminSettingsItems = [
    { icon: Building2, label: 'Group Management', id: 'group-management' },
    { icon: Users, label: 'User Management', id: 'user-management' },
    { icon: Package, label: 'Products/Campaigns', id: 'product-management' },
    { icon: ClipboardList, label: 'Product Details', id: 'product-details' },
    { icon: Target, label: 'Threshold Management', id: 'threshold-management' },
    { icon: Percent, label: 'Commission Ratio', id: 'commission-ratio-management' },
    { icon: UserPlus, label: 'Investor Management', id: 'investor-management' },
    { icon: BookOpen, label: 'Investor Enrollment', id: 'investor-enrollment' },
  ];
  const agentSettingsItems = [
    { icon: UserPlus, label: 'Investor Management', id: 'investor-management' },
    { icon: BookOpen, label: 'Investor Enrollment', id: 'investor-enrollment' },
  ];
  const settingsItems = isAdmin ? adminSettingsItems : agentSettingsItems;
  const showSettings = isAdmin || hasGroup;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`
          fixed top-0 left-0 z-50 w-64 h-screen neo-page transform transition-all duration-300 ease-in-out flex flex-col
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo and Close Button */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 lg:px-6" style={{ paddingBottom: '10px', paddingTop: '10px', paddingLeft: '55px' }}>
          <div className="flex items-center justify-start flex-1">
            <img 
              src="/images/YDG_LOGO.png" 
              alt="YDG Logo" 
              className={`h-14 lg:h-16 w-auto object-contain ${isDarkMode ? 'brightness-0 invert' : ''}`}
            />
          </div>
          <button 
            onClick={onClose}
            className="lg:hidden p-2 neo-icon-btn flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-dark-muted" />
          </button>
        </div>
        
        {/* Navigation Menu - Scrollable area between header and profile */}
        <nav className="flex-1 mt-4 lg:mt-6 px-3 overflow-y-auto">
          <ul className="space-y-2">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <li key={index}>
                  <button
                    type="button"
                    onClick={() => {
                      onNavigate(item.id);
                      onClose();
                    }}
                    className={`
                      w-full flex items-center px-3 py-2 lg:py-3 neo-nav text-sm font-medium transition-all duration-200 text-left
                      ${currentView === item.id 
                        ? 'neo-nav-active text-primary-700 dark:text-primary-400' 
                        : 'text-gray-600 dark:text-dark-muted hover:text-gray-900 dark:hover:text-dark-text'
                      }
                    `}
                  >
                    <Icon className={`w-5 h-5 mr-3 ${currentView === item.id ? 'text-primary-500 dark:text-primary-400' : 'text-gray-400 dark:text-dark-muted'}`} />
                    {item.label}
                  </button>
                </li>
              );
            })}
            
            {/* Settings Dropdown - Admin (full) or Agent (Investor Management only) */}
            {showSettings && (
              <li>
                <button
                  type="button"
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className={`
                    w-full flex items-center justify-between px-3 py-2 lg:py-3 neo-nav text-sm font-medium transition-all duration-200 text-left
                    ${(settingsItems.some((item) => item.id === currentView) || settingsOpen)
                      ? 'neo-nav-active text-primary-700 dark:text-primary-400' 
                      : 'text-gray-600 dark:text-dark-muted hover:text-gray-900 dark:hover:text-dark-text'
                    }
                  `}
                >
                  <div className="flex items-center">
                    <Settings className={`w-5 h-5 mr-3 ${
                      (settingsItems.some((item) => item.id === currentView) || settingsOpen) 
                        ? 'text-primary-500 dark:text-primary-400' 
                        : 'text-gray-400 dark:text-dark-muted'
                    }`} />
                    Settings
                  </div>
                  {settingsOpen ? (
                    <ChevronDown className="w-4 h-4 text-gray-400 dark:text-dark-muted" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400 dark:text-dark-muted" />
                  )}
                </button>
                
                {/* Settings Submenu */}
                {settingsOpen && (
                  <ul className="mt-2 ml-6 space-y-1">
                    {settingsItems.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <li key={index}>
                          <button
                            type="button"
                            onClick={() => {
                              onNavigate(item.id);
                              onClose();
                            }}
                            className={`
                              w-full flex items-center px-3 py-2 neo-nav text-sm font-medium transition-all duration-200 text-left
                              ${currentView === item.id 
                                ? 'neo-nav-active text-primary-700 dark:text-primary-400' 
                                : 'text-gray-600 dark:text-dark-muted hover:text-gray-900 dark:hover:text-dark-text'
                              }
                            `}
                          >
                            <Icon className={`w-4 h-4 mr-3 ${currentView === item.id ? 'text-primary-500 dark:text-primary-400' : 'text-gray-400 dark:text-dark-muted'}`} />
                            {item.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            )}
          </ul>
        </nav>
        
        {/* User Profile Section - Fixed at Bottom */}
        <div className="flex-shrink-0 w-full p-4 neo-page">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 neo-raised flex items-center justify-center rounded-full">
              <span className="text-primary-700 dark:text-primary-300 font-semibold text-sm">
                {user ? getInitials(user.name) : 'U'}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-dark-text">{user?.name || 'User'}</p>
              <p className="text-xs text-gray-500 dark:text-dark-muted">{user?.role || 'Role'}</p>
            </div>
          </div>
          <button 
            onClick={onLogout}
            className="flex items-center w-full px-3 py-2 text-sm font-medium neo-btn-danger"
          >
            <LogOut className="w-4 h-4 mr-3" />
            Sign Out
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
